// src/components/SubmoduleComponent.tsx
'use client';

import { GiftsModule, GiftVoucher, ModuleType, QueryKeys } from '@/enums';
import { useCategoryBrandFilterData, useCategoryProduct } from '@/hooks/product';
import { IconFilter } from '@/icons/IconFilter';
import { IconGrid1 } from '@/icons/IconGrid1';
import { IconGrid2 } from '@/icons/IconGrid2';
import { IconGrid4 } from '@/icons/IconGrid4';
import { IconGrid5 } from '@/icons/IconGrid5';
import { IKeyBannerProperty } from '@/interfaces/homepage';
import { IProductModel, ISearchProduct } from '@/interfaces/product';
import { searchProduct } from '@/services/product.service';
import useHeaderStore from '@/store/header';
import { setProductPosition } from '@/utils/helper';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useParams, useRouter, usePathname } from 'next/navigation';
import { useSearchParams } from '@/hooks/useCustomSearchParams';
import Breadcrumb from '@/components/Breadcrumb';
import { FilterContent } from '@/components/FilterPanel';
import { InfiniteScroll } from '@/components/InfiniteScroll';
import { ModuleBanner } from '@/components/ModuleBanner';
import ProductCard from '@/components/ProductCard';
import { BasicSelect } from '@/components/BasicSelect';
import useFooterStore from '@/store/footer';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { getDataLayer } from '@/utils/data-layer';
import CustomLink from './CustomLink';
import { useModuleBanner, useModuleCustomFilter } from '@/hooks/module';
import ShowFreeShippingBanner from './ShowFreeShippingBanner';
import { CountdownTimer } from './CountdownTimer';
import { useSelectedBanners } from '@/hooks/useSelectedBanners';
import { useDeliveryOption } from '@/hooks/useDeliveryOptions';
import { IconEmptyFolder } from '@/icons/IconEmptyFolder';

const SORT_OPTIONS = [
  { label: 'New Arrivals', value: 'new_arrivals' },
  { label: 'Best Sellers', value: 'best_sellers' },
  { label: 'Trending Now', value: 'trending_now' },
  { label: 'Price: Low to High', value: 'price_asc' },
  { label: 'Price: High to Low', value: 'price_desc' },
];

const DEFAULT_BANNERS = [{}] as IKeyBannerProperty[];

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
const SubmoduleComponent = () => {
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const module = params?.module as string;
  const submodule = params?.submodule as string;

  const typedFilterParam = params?.filterparam as string;
  const backupFilterParam = typedFilterParam;
  const splitFilterParams = typedFilterParam?.split('&');
  let tempFilters = '';
  let tempSortBy = '';
  if (typedFilterParam) {
    if (splitFilterParams.length > 1) {
      tempFilters = splitFilterParams[0].replaceAll(/_/g, '"')?.replaceAll(/~/g, ':');
      tempSortBy = splitFilterParams[1]?.split('=')?.[1];
    } else if (splitFilterParams.length === 1 && splitFilterParams[0].includes('sortby=')) {
      tempSortBy = splitFilterParams[0]?.split('=')?.[1];
    } else {
      tempFilters = splitFilterParams[0]?.replaceAll(/_/g, '"')?.replaceAll(/~/g, ':');
    }
  }

  let filtersFromPath = tempFilters;
  const queryClient = useQueryClient();
  const checkForDevice = () => {
    if (globalThis.window === undefined) return null;
    return window.innerWidth < 768;
  };
  const isMobile = checkForDevice();
  const [gridStyle, setGridStyle] = useState('grid-cols-2 md:grid-cols-3 lg:grid-cols-4');
  const [giftVoucher, setGiftVoucher] = useState<boolean>(
    !!(submodule?.toLocaleLowerCase().includes(GiftsModule.gift) && submodule?.toLocaleLowerCase().includes(GiftsModule.voucher)),
  );
  const [selectedGrid, setSelectedGrid] = useState(giftVoucher ? 4 : 5);

  const setFiltersAvailability = useHeaderStore((state) => state.setFiltersAvailability);
  const setFilterData = useHeaderStore((state) => state.setFilterData);
  const filterPanel = useHeaderStore((state) => state.filterPanel);
  const openFilterPanel = useHeaderStore((state) => state.openFilterPanel);
  const filterData = useHeaderStore((state) => state.filterData);
  const setFilterAttribute = useHeaderStore((state) => state.setFilterAttribute);
  const setPriceRange = useHeaderStore((state) => state.setPriceRange);
  const setStyleImageMapping = useHeaderStore((state) => state.setStyleImageMapping);
  const sortBy = useHeaderStore((state) => state.sortBy);
  const setShowloaderOverlay = useHeaderStore((state) => state.setShowloaderOverlay);
  const setClearPageIndex = useHeaderStore((state) => state.setClearPageIndex);
  const setColorDictionary = useHeaderStore((state) => state.setColorDictionary);
  const { selectedStore, myLocation, isExpressEnabled } = useDeliveryOption();
  const selectedStoreCodes = useHeaderStore((state) => state.selectedStoreCodes);
  const isStoreSelected = useHeaderStore((state) => state.isStoreSelected);
  const isStorePickEnabled = useHeaderStore((state) => state.showPickInStoreOption);
  const includeOtherStores = !(isStoreSelected || isExpressEnabled);

  const [pageIndex, setPageIndex] = useState<number>(searchParams?.get('pageIndex') ? Number.parseInt(`${searchParams?.get('pageIndex')}`) : 1);
  const pageSize = 20;
  const [sorting, setSorting] = useState<string>(searchParams?.get('sortby') || tempSortBy || 'new_arrivals');
  const getUpdatedFilters = () => {
    try {
      let parsedFilterFromPath: { [key: string]: any } = {};

      // Parse filters from path if they exist
      if (filtersFromPath) {
        try {
          parsedFilterFromPath = JSON.parse(filtersFromPath);
        } catch {
          // If parsing fails, filtersFromPath might not be valid JSON, skip it
        }
      }

      // Merge with filters from query params
      let filtersFromQuery = searchParams?.get('filters') as string;
      if (filtersFromQuery) {
        try {
          const queryFilters = JSON.parse(filtersFromQuery);
          parsedFilterFromPath = { ...parsedFilterFromPath, ...queryFilters };
        } catch {
          // If parsing fails, use only path filters
        }
      }

      let storeFilter: { [key: string]: any } = {};
      if (selectedStore?.Code && isStoreSelected) {
        storeFilter = { storeCodes: [selectedStore.Code] };
      } else if (selectedStoreCodes.length > 0) {
        storeFilter = { storeCodes: selectedStoreCodes };
      }

      const filterObject: { [key: string]: any } = {
        [module === ModuleType.Category ? 'categories' : 'brandSeName']: [submodule],
        ...parsedFilterFromPath,
        ...storeFilter,
      };

      // Remove any undefined values
      Object.keys(filterObject).forEach((key) => {
        if (filterObject[key] === undefined) {
          delete filterObject[key];
        }
      });

      // Use JSON.stringify to match server-side format exactly
      return JSON.stringify(filterObject);
    } catch (error) {
      // Fallback to original logic if anything fails
      console.error(error);
      let returnVal = '';
      let verifiedStrippedFiltersFromPathOrQuery = '';
      let filtersFromQuery = searchParams?.get('filters') as string;

      if (filtersFromPath && filtersFromQuery?.includes(filtersFromPath)) {
        verifiedStrippedFiltersFromPathOrQuery = filtersFromQuery.replace(/{|}/g, '');
      } else if (filtersFromPath) {
        verifiedStrippedFiltersFromPathOrQuery = filtersFromPath.replace(/{|}/g, '');
      }

      let storeFilterString = '';
      if (selectedStore?.Code && isStoreSelected) {
        storeFilterString = `,"storeCodes":["${selectedStore.Code}"]`;
      } else if (selectedStoreCodes.length > 0) {
        const storeCodes = selectedStoreCodes.map((id) => `"${id}"`).join(',');
        storeFilterString = `,"storeCodes":[${storeCodes}]`;
      }

      let allInclusiveFilters: string;

      const baseFilter = module === ModuleType.Category ? { categories: [submodule] } : { brandSeName: [submodule] };

      const filterObject: Record<string, any> = {
        ...baseFilter,
      };

      if (verifiedStrippedFiltersFromPathOrQuery) {
        const parsedFilters = JSON.parse(verifiedStrippedFiltersFromPathOrQuery);
        Object.assign(filterObject, parsedFilters);
      }

      if (storeFilterString) {
        const parsedStoreFilter = JSON.parse(storeFilterString);
        Object.assign(filterObject, parsedStoreFilter);
      }

      allInclusiveFilters = JSON.stringify(filterObject);

      if (filtersFromQuery) {
        let strippedAllInclusiveFilters = allInclusiveFilters.replace(/{|}/g, '');
        let strippedFiltersFromQuery = filtersFromQuery.replace(/{|}/g, '');
        if (strippedFiltersFromQuery === '') {
          returnVal = allInclusiveFilters;
        } else {
          if (strippedAllInclusiveFilters.includes(strippedFiltersFromQuery)) {
            const backupFilters =
              module == ModuleType.Category
                ? `{"categories":["${submodule}"]${storeFilterString}}`
                : `{"brandSeName":["${submodule}"]${storeFilterString}}`;
            strippedAllInclusiveFilters = backupFilters.replace(/{|}/g, '');
          }
          returnVal = `{${strippedAllInclusiveFilters},${strippedFiltersFromQuery}}`;
        }
      } else {
        returnVal = allInclusiveFilters;
      }

      returnVal = returnVal.replaceAll(',,', ',');
      return returnVal;
    }
  };

  const {
    isLoading: productListLoading,
    isFetching,
    data: productList = {} as ISearchProduct,
  } = useCategoryProduct(submodule, getUpdatedFilters(), 1, pageSize, sorting, module, selectedStore?.Code, includeOtherStores);

  useEffect(() => {
    if (isFetching) {
      setShowloaderOverlay(true);
    } else {
      setShowloaderOverlay(false);
    }
  }, [isFetching]);

  const { data: permanentFilterData = {} as ISearchProduct } = useCategoryBrandFilterData(
    submodule,
    module == ModuleType.Category ? `{"categories":["${submodule}"]}` : `{"brandSeName":["${submodule}"]}`,
    1,
    pageSize,
    tempSortBy || 'new_arrivals',
    module,
    selectedStore?.Code,
    includeOtherStores,
  );

  const { data: moduleBanners = DEFAULT_BANNERS } = useModuleBanner({
    type: module,
    seName: submodule?.toLowerCase(),
  });
  const { selectedBanners, isTab } = useSelectedBanners(moduleBanners);
  const { data: moduleCustomFilter } = useModuleCustomFilter(module, submodule, true);
  const [rightPosition, setRightPosition] = useState(11);
  const [left, right] = setProductPosition(productList, isTab, rightPosition);
  const leftLargeProductPosition = new Set<number>(selectedGrid === 5 ? left : []);
  const rightLargeProductPosition = new Set<number>(selectedGrid === 5 ? right : []);
  const [hideFilter, setHideFilter] = useState(giftVoucher ? true : false);
  const [hasMore, setHasMore] = useState(productList?.HasNextPage || false);
  const [selectedCtaButton, setSelectedCtaButton] = useState(`/${module}/${submodule}?filters=${searchParams?.get('filters')}`);
  const [isRefetchOnMount, setIsRefetchOnMount] = useState<boolean>(true);
  const { setShowFullFooter } = useFooterStore((store) => store);

  useEffect(() => {
    setRightPosition(isTab ? 7 : 11);
    setFiltersAvailability(true);

    return () => {
      setFiltersAvailability(false);
      setFilterData('');
    };
  }, []);

  useEffect(() => {
    if (submodule?.toLocaleLowerCase().includes(GiftVoucher.gift) && submodule?.toLocaleLowerCase().includes(GiftVoucher.voucher)) {
      setHideFilter(true);
      setGiftVoucher(true);
      setSelectedGrid(4);
      setGridViewStyle(4);
    } else {
      setHideFilter(false);
      setGiftVoucher(false);
      setSelectedGrid(5);
      setGridViewStyle(5);
    }
  }, [submodule, module, filtersFromPath, router]);

  useEffect(() => {
    setSelectedCtaButton(`/${module}/${submodule}?filters=${searchParams?.get('filters')}`);
    setPageIndex(1);
  }, [searchParams]);

  useEffect(() => {
    if (sortBy) {
      setSorting(sortBy);
    }
  }, [sortBy]);

  useEffect(() => {
    let gridValue;

    if (isMobile) {
      gridValue = 2;
    } else if (submodule?.toLocaleLowerCase().includes(GiftVoucher.gift) && submodule?.toLocaleLowerCase().includes(GiftVoucher.voucher)) {
      gridValue = 4;
    } else {
      gridValue = 5;
    }

    setSelectedGrid(gridValue);
  }, [submodule, isMobile, module, filtersFromPath]);

  useEffect(() => {
    setHasMore(productList?.HasNextPage || false);
  }, [productList?.FilterAttributes, productList?.HasNextPage]);

  useEffect(() => {
    if (Object.keys(permanentFilterData).length > 0) {
      setFilterAttribute(permanentFilterData?.FilterAttributes);
      setStyleImageMapping(permanentFilterData?.StyleImageMapping);
      setColorDictionary(permanentFilterData?.ColorHexDictionary);
    }
  }, [permanentFilterData]);

  useEffect(() => {
    if (productList.Products && isRefetchOnMount) {
      setIsRefetchOnMount(false);
      const itemArray = productList?.Products?.map((product) => {
        let obj: { [key: string]: string } = {};

        if (product?.CategoryName) {
          let splittedCategories = product?.CategoryName.split(',');
          splittedCategories?.forEach((category, categoryIndex) => {
            obj['item_category' + (categoryIndex ? categoryIndex : '')] = category;
          });
        }

        let returnVal = {
          item_id: product?.SeName,
          item_name: product?.Name,
          item_sku: product?.Sku,

          price: product?.ProductPrice.Price,
          quantity: product?.StockQuantity,
          item_variant: product?.SeName,
          color: product?.ColorVariants,
          brand: product?.BrandName,
          gender: giftVoucher ? undefined : product?.Gender,
          size: product?.Sizes,
          ...obj,
        };
        if (giftVoucher) {
          delete returnVal.gender;
        }
        return returnVal;
      });
      const dataLayer = getDataLayer();

      dataLayer?.push({
        event: 'view_item_list',
        ecommerce: {
          items: itemArray,
        },
      });
    }

    // close filter panel when filter panel is open and product list is loaded
    if (filterPanel) openFilterPanel(false);
  }, [productList]);

  const setGridViewStyle = (type: number) => {
    switch (type) {
      case 1:
        setSelectedGrid(1);
        setGridStyle('grid-cols-1');
        break;
      case 2:
        setSelectedGrid(2);
        setGridStyle('grid-cols-2');
        break;
      case 4:
        setSelectedGrid(4);
        setGridStyle('grid-cols-2 lg:grid-cols-4');
        break;
      case 5:
        setSelectedGrid(5);
        setGridStyle('grid-cols-2 lg:grid-cols-4');
        break;

      default:
        setSelectedGrid(5);
        setGridStyle('grid-cols-3 lg:grid-cols-4');
        break;
    }
  };

  useEffect(() => {
    let tempFilerString = searchParams?.get('filters') as string;
    // Don't run if searchParams isn't loaded yet to avoid overwriting URL with defaults
    if (!searchParams) return;

    // Only run if checking for devices is done
    if (typeof isMobile !== 'boolean') return;

    if ((filterData != '{}' && filterData != '') || tempFilerString || sorting) {
      replacePath();
    }
  }, [filterData, submodule, module, filtersFromPath, sorting]); // Removed searchParams dependency
  
  useEffect(() => {
    setPageIndex(1);
  }, [isStoreSelected, isExpressEnabled, selectedStore?.Code, filtersFromPath, sorting, submodule]);

  useEffect(() => {
    let minPrice = permanentFilterData?.PriceRange?.Min;
    let maxPrice = permanentFilterData?.PriceRange?.Max;
    console.log(minPrice, maxPrice, 'pricing');
    setPriceRange({ Min: minPrice, Max: maxPrice });
  }, [permanentFilterData?.PriceRange]);

  const replacePath = () => {
    if (!searchParams) return;

    const queryKeys = new Set(['module', 'submodule', 'pageIndex', 'filters', 'filterparam', 'sortby']);
    let extraQueryParams = '';
    searchParams?.forEach((value, key) => {
      if (!queryKeys.has(key)) {
        extraQueryParams += `&${key}=${value}`;
      }
    });

    const urlSlice = module === ModuleType.Category ? '/category' : '/brand';
    let newPath = `${urlSlice}/${submodule}${backupFilterParam ? '/' + backupFilterParam : ''}`;
    let queryString = '';

    // Use URL filters if store is uninitialized (empty string)
    // '{}' means explicitly empty filters (user cleared them)
    const effectiveFilterData = filterData === '' ? searchParams?.get('filters') || '' : filterData;

    if (pageIndex == 1 && (effectiveFilterData == '' || effectiveFilterData == '{}')) {
      queryString = `sortby=${sorting}${extraQueryParams}`;
    } else if (pageIndex == 1 && effectiveFilterData != '') {
      queryString = `filters=${effectiveFilterData}&sortby=${sorting}${extraQueryParams}`;
    } else if (pageIndex > 1 && (effectiveFilterData == '' || effectiveFilterData == '{}')) {
      queryString = `pageIndex=${pageIndex}&sortby=${sorting}${extraQueryParams}`;
    } else if (pageIndex > 1 && effectiveFilterData != '') {
      queryString = `pageIndex=${pageIndex}&filters=${effectiveFilterData}&sortby=${sorting}${extraQueryParams}`;
    }

    const newUrl = `${newPath}${queryString ? '?' + queryString : ''}`;

    // Fix: Prevent redundant router replacements that cause component unmounting loops
    const currentParams = new URLSearchParams(searchParams?.toString());
    const newParams = new URLSearchParams(queryString);
    // Sort params to ensure order doesn't affect equality
    currentParams.sort();
    newParams.sort();

    if (pathname === newPath && currentParams.toString() === newParams.toString()) {
      return;
    }

    router.replace(newUrl, { scroll: false });
  };

  useEffect(() => {
    if (!searchParams) return;

    // Sync sorting if searchParams loads later
    const sortFromUrl = searchParams.get('sortby');
    if (sortFromUrl && sortFromUrl !== sorting) {
      setSorting(sortFromUrl);
    } else if (!sortFromUrl) {
      // If no sort param in URL, trigger replacePath to set default 'new_arrivals'
      // This restores the original behavior of adding ?sortby=new_arrivals when coming from menu
      replacePath();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const notificationShownRef = useRef(false);

  useEffect(() => {
    if (!productListLoading && productList && productList?.Products.length === 0 && !notificationShownRef.current) {
      if (!isStoreSelected && !isExpressEnabled) {
        notificationShownRef.current = true;
        (async () => {
          await delay(100);
          openNotification('danger', 'Category', 'Category is not available');
          await delay(1000);
          router.replace('/', { scroll: false });
        })();
      }
    }
  }, [productList, productListLoading]);

  useEffect(() => {
    if (isStoreSelected && isStorePickEnabled) {
      openNotification('success', 'Products', 'You are seeing items based on your selected store. Remove store to view full inventory.');
    }
  }, [isStoreSelected]);

  async function fetchMoreData() {
    try {
      const queryKeys = [QueryKeys.singleModuleProductList, module, submodule, getUpdatedFilters(), !isStoreSelected && !isExpressEnabled];
      await searchProduct('', getUpdatedFilters(), pageIndex + 1, pageSize, sorting, includeOtherStores, false).then(
        (newProducts: ISearchProduct) => {
          queryClient.setQueryData(queryKeys, () => {
            setHasMore(newProducts?.HasNextPage || false);
            setShowFullFooter(newProducts?.HasNextPage || false);
            productList.Products = [...(productList?.Products || []), ...newProducts.Products];
            setPageIndex(pageIndex + 1);
            return { ...productList };
          });
        },
      );
    } catch (c) {
      console.log(c, 'error');
    }
  }

  return (
    <div>
      {selectedBanners?.map((selectedBanner, index) => {
        return (
          <Fragment key={index}>
            <Breadcrumb className="container" bred={moduleCustomFilter?.Breadcrumbs ?? []} />
            {selectedBanner?.Url ? (
              <CustomLink prefetch={false} href={selectedBanner?.Url}>
                <ModuleBanner banner={selectedBanner}>
                  <div
                    className="absolute inset-0 flex items-start justify-end py-4 md:py-8"
                    dangerouslySetInnerHTML={{ __html: selectedBanner?.Text }}
                  />
                </ModuleBanner>
              </CustomLink>
            ) : (
              <ModuleBanner banner={selectedBanner} />
            )}
            <CountdownTimer content={selectedBanner} isHomepage={false} />
          </Fragment>
        );
      })}
      {!selectedBanners && (
        <>
          <Breadcrumb className="container" bred={moduleCustomFilter?.Breadcrumbs || []} />
          <h1
            className={`container text-3xl font-semibold capitalize md:text-4xl lg:text-5xl ${
              moduleCustomFilter?.Filters?.length && moduleCustomFilter?.Filters?.length > 0 ? 'md:mb-8' : ''
            }`}
          >
            {moduleCustomFilter?.Name}
          </h1>
        </>
      )}
      <div className="container">
        <div className="module-custom-filter-button">
          {moduleCustomFilter?.Filters?.length && moduleCustomFilter?.Filters?.length > 0 ? (
            <div className="mt-6 flex w-full flex-wrap justify-center gap-6 text-xl font-bold max-md:hidden md:mt-8 lg:mt-12">
              {moduleCustomFilter.Filters.map((x) => {
                return (
                  <CustomLink
                    prefetch={false}
                    key={x?.Name}
                    className={`flex h-[44px] w-[230px] max-w-[230px] items-center justify-center rounded border border-black uppercase transition duration-300 ease-linear hover:bg-black hover:text-white focus:bg-black focus:text-white ${
                      x?.RelativeFilterUrl == selectedCtaButton ? 'bg-black text-white' : ''
                    }`}
                    href={x?.RelativeFilterUrl || '#'}
                    onClick={() => setSelectedCtaButton(x?.RelativeFilterUrl)}
                  >
                    {x?.Name}
                  </CustomLink>
                );
              })}
            </div>
          ) : (
            ''
          )}
        </div>

        <div
          className={`module-header mb-8 flex justify-between ${selectedBanners ? 'mt-6 md:mt-8 lg:mt-12' : 'mt-12'} ${productList?.HasCategoryFreeShipping === false ? 'mb-8 md:mb-10 lg:mb-12' : ''} `}
        >
          {/* filter and results */}
          <div className="flex items-center">
            <div
              className={`border-r border-[#e5e5e5] transition-all ${hideFilter && !isMobile && giftVoucher ? 'w-[8rem] p-3' : 'p-6 pl-0 md:w-[13rem] xl:w-[22rem]'}`}
            >
              <div className="hidden w-full justify-between md:flex">
                <p
                  className={`whitespace-nowrap text-xl ${hideFilter || giftVoucher ? 'pointer-events-none w-0 opacity-0' : 'w-full opacity-100'}`}
                  style={hideFilter ? { transition: 'width 500ms, opacity 200ms' } : { transition: 'width 500ms, opacity 200ms 200ms' }}
                >
                  Hide Filters
                </p>
                <div className={`flex w-full items-center ${hideFilter ? 'justify-center' : 'justify-end'}`}>
                  <IconFilter
                    className={`w-8 ${giftVoucher ? 'opacity-50' : 'cursor-pointer'}`}
                    onClick={() => {
                      if (!giftVoucher) {
                        hideFilter ? setHideFilter(false) : setHideFilter(true);
                      }
                    }}
                  />
                </div>
              </div>
              <IconFilter
                className={`w-8 md:hidden ${isMobile === false && giftVoucher ? 'opacity-50' : 'cursor-pointer'}`}
                onClick={() => {
                  if (!giftVoucher) {
                    openFilterPanel(true);
                  } else if (giftVoucher && isMobile) {
                    openFilterPanel(true);
                  }
                }}
              />
            </div>
          </div>
          {/* cta button */}
          <div className={`flex w-full items-center justify-between px-6`}>
            {selectedBanners && (
              <h1 className={`text-center text-3xl font-semibold capitalize md:text-4xl lg:text-5xl`}>{moduleCustomFilter?.AlternateName}</h1>
            )}
            <div className="flex items-center justify-center space-x-6">
              <IconGrid5
                className={`w-6 max-md:hidden ${selectedGrid === 5 ? 'text-black' : 'text-gray-400'} ${giftVoucher ? 'opacity-50' : 'a'}`}
                onClick={() => {
                  !giftVoucher && setGridViewStyle(5);
                }}
              />
              <IconGrid2
                className={`w-6 max-md:hidden ${selectedGrid === 2 ? 'text-black' : 'text-gray-400'} ${giftVoucher ? 'opacity-50' : 'a'}`}
                onClick={() => {
                  !giftVoucher && setGridViewStyle(2);
                }}
              />
              <IconGrid4
                className={`w-6 max-lg:hidden ${selectedGrid === 4 ? 'text-black' : 'text-gray-400'}`}
                onClick={() => {
                  setGridViewStyle(4);
                }}
              />
            </div>
            <div className="flex items-center justify-center md:hidden">
              <IconGrid1
                className={`a mr-4 w-6 md:mr-7 ${selectedGrid === 1 ? 'text-black' : 'text-gray-400'}`}
                onClick={() => setGridViewStyle(1)}
              />
              <IconGrid2 className={`a w-6 md:mr-7 ${selectedGrid === 2 ? 'text-black' : 'text-gray-400'}`} onClick={() => setGridViewStyle(2)} />
            </div>
          </div>
          <div className="hidden min-w-[220px] items-center justify-between border-l border-[#e5e5e5] px-3 md:flex">
            <div className="text-xl">Sort By:</div>
            <BasicSelect
              className="my-auto w-auto px-4 text-xl"
              name="Sort By"
              value={sorting}
              unstyled
              searchable={false}
              initialValue={sorting}
              options={SORT_OPTIONS}
              onChange={(e: any) => {
                setClearPageIndex(true);
                setSorting(e?.value);
              }}
            />
          </div>
        </div>
        <ShowFreeShippingBanner show={productList?.HasCategoryFreeShipping === true} />
      </div>
      <div className="container">
        <div className="flex w-full">
          <div
            className={`hidden md:block ${hideFilter ? 'w-0 opacity-0' : 'opacity-100 md:w-[26rem] lg:w-[27.5rem]'}`}
            style={hideFilter ? { transition: 'width 500ms, opacity 200ms' } : { transition: 'width 500ms, opacity 200ms 200ms' }}
          >
            {!giftVoucher && <FilterContent className="" asContent={true} initialFilterData={permanentFilterData?.FilterAttributes} />}
          </div>

          <div className={`w-full ${hideFilter ? 'w-full' : 'md:ml-10'}`}>
            {productList?.TotalItems > 0 ? (
              <>
                <InfiniteScroll
                  hasMore={hasMore}
                  loadMoreFunc={fetchMoreData}
                  isLoading={productListLoading}
                  loaderElement={<button title="Loading" className={`loading w-full p-16 text-xl text-black 2xl:text-2xl 3xl:text-3xl`}></button>}
                >
                  <div className={`grid gap-x-[1rem] gap-y-[1rem] ${gridStyle}`}>
                    {productList?.Products?.map((item: IProductModel, index: number) => {
                      return (
                        <div
                          key={item.SeName + index}
                          className={`${
                            selectedGrid === 5
                              ? `${leftLargeProductPosition.has(index) ? 'relative col-span-2 row-span-2 h-full w-full' : ''} ${
                                  rightLargeProductPosition.has(index) ? 'relative col-span-2 row-span-2 h-full w-full' : ''
                                }`
                              : ``
                          } `}
                        >
                          <ProductCard
                            className="p-y-0 flex h-full w-full flex-col"
                            key={item.SeName + index}
                            name={item.Name}
                            url={`/product/${item.SeName}`}
                            category={item?.CategoryName}
                            options={item?.Colors}
                            imageUrl={item.FeaturedImageUrl}
                            imageAlt={item?.AltText || item.Name}
                            price={item.ProductPrice?.Price}
                            oldPrice={item?.ProductPrice?.OldPrice}
                            seName={item.SeName}
                            shortDescription={item?.ShortDescription}
                            colorVariants={true}
                            ColorVariants={item?.ColorVariants}
                            largeSize={leftLargeProductPosition.has(index) || rightLargeProductPosition.has(index) ? true : false}
                            isCategoryVisible={false}
                            neuroMarketingMessage={item?.NeuroMarketingMessage}
                            neuroMarketingSettings={productList?.NeuroMarketingSettings}
                            HasFreeShipping={item?.IsFreeShipping}
                            AvailableInProvidedStore={item?.AvailableInProvidedStore && Boolean(myLocation.value)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </InfiniteScroll>
                <div
                  className="my-9 text-justify"
                  dangerouslySetInnerHTML={{ __html: moduleCustomFilter?.Description ? moduleCustomFilter?.Description : '' }}
                />
              </>
            ) : (
              <div className="mt-8 flex flex-col items-center justify-center gap-y-8">
                <IconEmptyFolder className="h-fit w-64 md:w-80" />

                <p className="text-center text-[16px] font-semibold opacity-100">No product Available</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <style jsx>{`
        .hide-scroll::-webkit-scrollbar {
          display: none;
        }
        .hide-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .module-header {
          border: 1px solid #e5e5e5;
          border-right: none;
          border-left: none;
        }

        .category-description :global(p),
        .category-description :global(ul),
        .category-description :global(ol) {
          margin-bottom: 1em;
        }

        .category-description :global(ul),
        .category-description :global(ol) {
          padding-left: 30px;
        }

        .category-description :global(ol) {
          list-style-type: decimal;
        }

        .product-divider {
          font-size: 1.75rem;
          text-align: center;
          position: relative;
          margin-bottom: 20px;
        }
        .product-divider:before {
          content: '';
          position: absolute;
          background: #e5e7eb;
          left: 0;
          width: 100%;
          height: 1px;
          top: calc(50% - 0.5px);
        }
        .product-divider span {
          padding: 0 15px;
          background-color: #fff;
          position: relative;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};
export default SubmoduleComponent;
